import { useCallback } from 'react';
// import { useSignals } from '@preact/signals-react/runtime';
import { createGlobalState } from 'react-use';
import configData from '../signals/configData';

// Hold references to in-flight tracking pixels to prevent GC on some mobile browsers
const inflightPixels = [];

const initialState = {
	uuid: '',
	lastActionTimestamp: '',
};

const useRecordState = createGlobalState(initialState);

const generateUUID = () => {
	return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Format a Date as ISO-8601 string in GMT+8 timezone (e.g., 2025-09-18T12:34:56+08:00)
const toGmt8ISOString = (date = new Date()) => {
	const tzOffsetMinutes = 8 * 60; // GMT+8
	// shift the time by +08:00 relative to UTC, then format using UTC getters
	const shifted = new Date(date.getTime() + tzOffsetMinutes * 60 * 1000);

	const yyyy = shifted.getUTCFullYear();
	const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
	const dd = String(shifted.getUTCDate()).padStart(2, '0');
	const HH = String(shifted.getUTCHours()).padStart(2, '0');
	const MM = String(shifted.getUTCMinutes()).padStart(2, '0');
	const SS = String(shifted.getUTCSeconds()).padStart(2, '0');

	return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}+08:00`;
};

const useRecord = () => {
	// useSignals();

	const {
		Timeout,
		exhID,
		// recordAPI, recordUser, recordPassword
	} = configData.value;
	const [state, setState] = useRecordState(initialState);

	const getUUID = useCallback(() => {
		let uuid = '';

		if (!state.uuid) {
			uuid = generateUUID();
			setState((state) => {
				return {
					...state,
					uuid,
					lastActionTimestamp: Date.now(),
				};
			});
		}

		if (state.uuid) {
			const now = Date.now();
			const lastActionTimestamp = state.lastActionTimestamp;
			const timeDiff = now - lastActionTimestamp;

			if (timeDiff > Timeout) {
				uuid = generateUUID();
				setState((state) => {
					return {
						...state,
						uuid,
						lastActionTimestamp: Date.now(),
					};
				});
			}

			if (timeDiff <= Timeout) {
				uuid = state.uuid;
				setState((state) => {
					return {
						...state,
						lastActionTimestamp: Date.now(),
					};
				});
			}
		}

		return uuid;
	}, [state.uuid, state.lastActionTimestamp, setState, Timeout]);

	const makeRecord = useCallback(
		({ Action, Booth, Product, Keyword }) => {
			const params = new URLSearchParams(window.location.search);
			const location = params.get('location');
			const start = params.get('start');
			const Location = localStorage.getItem('Location') || location;
			const Start = localStorage.getItem('Start') || start;
			const uuid = getUUID();
			const record = {
				EXID: exhID,
				UUID: uuid,
				Action,
				Location,
				Booth: '',
				Keyword: '',
				Start: '',
				Product: '',
				Timestamp: toGmt8ISOString(),
			};

			if (Action === 'F') {
				record.Booth = Booth;
			}

			if (Action === 'S') {
				record.Keyword = Keyword;
			}

			if (Action === 'N') {
				record.Start = Start;
				record.Booth = Booth;
			}

			if (Action === 'T') {
				record.Booth = Booth;
				record.Product = Product || '';
			}

			// Get spreadsheet ID and sheet name from config
			const { googleScriptUrl, spreadsheetId, sheetName } = configData.value;

			// Create a simple image request to avoid CORS issues
			// This is a common technique for logging data without expecting a response
			const sendDataToGoogleScript = (url, data) => {
				// Create a URL with query parameters
				const params = new URLSearchParams();
				params.append('spreadsheetId', spreadsheetId);
				params.append('sheetName', sheetName);
				params.append('recordData', JSON.stringify(data));

				// Create a cachebuster to prevent caching
				params.append('cachebuster', new Date().getTime());

				// Create an image element to make the request
				const img = new Image();

				// Keep a reference until request settles (mobile Safari/Chrome can GC otherwise)
				inflightPixels.push(img);

				// Set the source to the Google Apps Script URL with parameters
				img.src = `${url}?${params.toString()}`;

				// Optional: basic diagnostics (will not affect request)
				img.onload = () => {
					// Image loaded successfully; request reached the server.
					// No further action required.
					const idx = inflightPixels.indexOf(img);
					if (idx !== -1) inflightPixels.splice(idx, 1);
				};
				img.onerror = () => {
					// Even if the image fails to load (due to CORS/opaque response),
					// the GET request is still issued and typically processed server-side.
					// Intentionally silent to avoid noisy logs in production.
					const idx = inflightPixels.indexOf(img);
					if (idx !== -1) inflightPixels.splice(idx, 1);
				};
			};

			// Send data to Google Apps Script
			sendDataToGoogleScript(googleScriptUrl, record);
		},
		[exhID, getUUID]
	);

	return {
		makeRecord,
		userid: state.uuid,
		uuid: exhID,
	};
};
export default useRecord;
